import { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import { Button, Dropdown, Skeleton } from '@openedx/paragon';
import {
  RiCalendarLine, RiBookOpenLine, RiMedalLine, RiFireLine,
} from '@remixicon/react';

import messages from '../messages';
import { useMyPoints, LEADERBOARD_WINDOWS } from '../leaderboardHooks';

import './MyPointsCard.scss';

const WINDOW_OPTIONS = [
  { key: LEADERBOARD_WINDOWS.WEEK, msg: 'pointsWindowWeek' },
  { key: LEADERBOARD_WINDOWS.MONTH, msg: 'pointsWindowMonth' },
  { key: LEADERBOARD_WINDOWS.ALL, msg: 'pointsWindowAll' },
];

/**
 * Personal points summary for the learner: total points, rank, and the
 * completion / skills / streak breakdown for a chosen calendar window.
 * Visually mirrors the Progress page cards (StatsCard / ActivityMap).
 * Self-hides when the leaderboard feature is off (the caller passes `enabled`).
 */
const MyPointsCard = ({ enabled = true }) => {
  const intl = useIntl();
  const { data, isLoading } = useMyPoints(enabled);
  const [window, setWindow] = useState(LEADERBOARD_WINDOWS.WEEK);

  if (!enabled) {
    return null;
  }

  const activeOption = WINDOW_OPTIONS.find((o) => o.key === window);
  const stats = data?.windows?.[window];
  const totalPoints = stats?.total_points ?? 0;
  const rank = stats?.rank ?? null;
  const totalUsers = stats?.total_users ?? 0;

  const breakdown = [
    {
      key: 'completion', icon: RiBookOpenLine, msg: 'pointsBreakdownCompletion', value: stats?.block_points ?? 0,
    },
    {
      key: 'skills', icon: RiMedalLine, msg: 'pointsBreakdownSkills', value: stats?.skill_points ?? 0,
    },
    {
      key: 'streak', icon: RiFireLine, msg: 'pointsBreakdownStreak', value: stats?.streak_points ?? 0,
    },
  ];

  return (
    <>
      <div className="my-points-card__section-header">
        <h2 className="my-points-card__section-title">
          {intl.formatMessage(messages.myPointsTitle)}
        </h2>
        <Dropdown>
          <Dropdown.Toggle
            id="my-points-window"
            as={Button}
            variant="ghost-secondary"
            size="sm"
            iconBefore={RiCalendarLine}
          >
            {intl.formatMessage(messages[activeOption.msg])}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {WINDOW_OPTIONS.map((option) => (
              <Dropdown.Item
                key={option.key}
                active={option.key === window}
                onClick={() => setWindow(option.key)}
              >
                {intl.formatMessage(messages[option.msg])}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="my-points-card__card">
        {isLoading ? (
          <Skeleton height={120} />
        ) : (
          <>
            <div className="my-points-card__headline">
              <div className="my-points-card__total">
                <span className="my-points-card__total-value">{totalPoints}</span>
                <span className="my-points-card__total-label">
                  {intl.formatMessage(messages.myPointsTotalLabel)}
                </span>
              </div>
              <span className="my-points-card__rank">
                {rank
                  ? intl.formatMessage(messages.myPointsRank, { rank, total: totalUsers })
                  : intl.formatMessage(messages.myPointsUnranked)}
              </span>
            </div>
            <div className="my-points-card__breakdown">
              {breakdown.map(({
                key, icon: IconComponent, msg, value,
              }) => (
                <div key={key} className="my-points-card__stat">
                  <IconComponent className="my-points-card__stat-icon" size={18} />
                  <span className="my-points-card__stat-value">{value}</span>
                  <span className="my-points-card__stat-label">
                    {intl.formatMessage(messages[msg])}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

MyPointsCard.propTypes = {
  enabled: PropTypes.bool,
};

MyPointsCard.defaultProps = {
  enabled: true,
};

export default MyPointsCard;
